import { PIPELINE_TOOL_NAMES } from '@kw/quokka-ui';
import _ from 'lodash';
import { OrgPolicyResults } from '../types/ApplicationDetailsType';

export const getReviewActionOptions = (hasPermission: boolean, setIsOpenReviewModal: (isOpen: boolean) => void) =>
  hasPermission
    ? [
        {
          label: 'Review Rule',
          value: 'review_rule',
          onClick: () => setIsOpenReviewModal(true),
        },
      ]
    : [];

export const getCategoryFromRuleId = ruleId => {
  const parts = ruleId.split('/');
  if (parts[0] === 'profile' || parts[0] === 'audit') {
    return parts[1] || parts[0];
  }
  return parts[0];
};

export const isBehavioral = ruleSummary =>
  (ruleSummary.rule?.tool === PIPELINE_TOOL_NAMES.pattern ||
    ruleSummary.rule?.tool === `${PIPELINE_TOOL_NAMES.pattern}-ios` ||
    ruleSummary.rule?.tool === PIPELINE_TOOL_NAMES.permission) &&
  ruleSummary.rule?.id?.startsWith('profile');

// Predicates for filtering rule summaries by tab/section. Single source of truth used by:
// - OverviewTab (Rules + Permissions sub-tabs), RootTabs (Behaviors tab)
// - SarifReportContent (mapping triggered policies to the tab they surface in)
export const PERMISSION_OVERVIEW_RULE_IDS = ['profile/system/requests-privileged-permissions'];

export const isRuleOverviewRuleSummary = ruleSummary =>
  ruleSummary?.rule?.properties?.category === 'security' || ruleSummary?.rule?.properties?.category === 'privacy';

export const isPermissionOverviewRuleSummary = ruleSummary =>
  ruleSummary?.rule?.tool === 'permoid' ||
  ruleSummary?.rule?.tool === 'permoid-ios' ||
  PERMISSION_OVERVIEW_RULE_IDS.includes(ruleSummary?.rule?.id);

export const isBehavioralRuleSummary = ruleSummary =>
  (ruleSummary?.rule?.tool === 'pulsar' || ruleSummary?.rule?.tool === 'pulsar-ios') &&
  ruleSummary?.rule?.properties?.category === 'behavioral';

export const buildCustomRulesObject = ({ possibleForeignConnectionRule, highRiskCountriesConnectionRule }) => {
  const rules = {};

  if (possibleForeignConnectionRule?.foreignConnections?.length > 0) {
    const [dynamicForeignConnections, staticForeignConnections] = _.partition(
      possibleForeignConnectionRule.foreignConnections,
      entry => entry?.isDynamic === true,
    );
    if (dynamicForeignConnections.length > 0) {
      rules['possible-foreign-connection'] = {
        evidence: dynamicForeignConnections,
        metadata: possibleForeignConnectionRule?.foreignConnectionRuleMetadata,
      };
    }
    if (staticForeignConnections.length > 0) {
      rules['static-possible-foreign-connection'] = {
        evidence: staticForeignConnections,
        metadata: possibleForeignConnectionRule?.foreignConnectionRuleMetadata,
      };
    }
  }

  if (highRiskCountriesConnectionRule?.highRiskCountryConnections?.length > 0) {
    const [dynamicHighRiskCountriesConnections, staticHighRiskCountriesConnections] = _.partition(
      highRiskCountriesConnectionRule.highRiskCountryConnections,
      entry => entry?.isDynamic === true,
    );
    if (dynamicHighRiskCountriesConnections.length > 0) {
      rules['highrisk-country-connection'] = {
        evidence: dynamicHighRiskCountriesConnections,
        metadata: highRiskCountriesConnectionRule?.highRiskCountryConnectionRuleMetadata,
      };
    }
    if (staticHighRiskCountriesConnections.length > 0) {
      rules['static-highrisk-country-connection'] = {
        evidence: staticHighRiskCountriesConnections,
        metadata: highRiskCountriesConnectionRule?.highRiskCountryConnectionRuleMetadata,
      };
    }
  }

  return rules;
};

const appendPolicyNameForRule = (map: Record<string, string[]>, ruleId: string, policyName: string) => {
  const policyNames = map[ruleId] ?? [];
  if (!policyNames.includes(policyName)) {
    policyNames.push(policyName);
  }
  map[ruleId] = policyNames;
};

const addViolationToPolicyMap = (map: Record<string, string[]>, violation: OrgPolicyResults) => {
  for (const rule of violation.associatedRules) {
    for (const ruleIds of Object.values(rule.appIorResultsMap)) {
      if (!ruleIds) continue;
      for (const ruleId of ruleIds) {
        if (!ruleId) continue;
        appendPolicyNameForRule(map, ruleId, violation.policyName);
      }
    }
  }
};

// returns a map of rule id to violated policy names (multiple policies can reference the same rule)
export const buildPolicyViolationMap = (policyViolations: OrgPolicyResults[] = []) => {
  const map: Record<string, string[]> = {};
  for (const violation of policyViolations) {
    if (!violation.didTrigger) continue;
    addViolationToPolicyMap(map, violation);
  }
  return map;
};
